/**
 * 企業関連のQuery Hooks
 * TanStack Queryを使用したデータフェッチングとキャッシュ管理
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getCompaniesAuthenticated,
  getCompanyByIdAuthenticated,
  createCompanyAuthenticated,
  updateCompanyAuthenticated,
  deleteCompanyAuthenticated,
  forceDeleteCompanyAuthenticated,
  getCompanyAccountsAuthenticated,
  getCompanyUsersAuthenticated,
  getUnlinkedAccountsAuthenticated,
  linkAccountToCompanyAuthenticated,
  executeBatchAuthenticated,
  getCompanyEmailTemplatesAuthenticated,
  updateCompanyEmailTemplatesAuthenticated,
} from '@/lib/api/mise/companies';
import { companyKeys } from '../keys';
import { CACHE_TIME } from '../client';
import type {
  Company,
  CompanyWithStats,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyAccount,
  CompanyUser,
  UpdateEmailTemplatesRequest,
} from '@/lib/types/mise/company';

/**
 * 企業一覧を取得するQuery Hook
 * @param search 検索キーワード（オプション）
 */
export function useCompanies(search?: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: companyKeys.list(search),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getCompaniesAuthenticated(authHeaders, { search });
    },
    enabled: !authHeaderLoading && isAuthorized, // 認証状態が準備完了してから実行
    staleTime: CACHE_TIME.ACCOUNTS_LIST, // アカウント一覧と同じキャッシュ時間を使用
  });
}

/**
 * 企業詳細を取得するQuery Hook
 * @param id 企業ID
 */
export function useCompany(id: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getCompanyByIdAuthenticated(id, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!id, // 認証状態が準備完了し、IDが存在する場合のみ実行
    staleTime: CACHE_TIME.ACCOUNT_DETAIL, // アカウント詳細と同じキャッシュ時間を使用
  });
}

/**
 * 企業を作成するMutation Hook
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (request: CreateCompanyRequest) => {
      const authHeaders = getAuthHeader();
      return createCompanyAuthenticated(request, authHeaders);
    },
    // 企業一覧のキャッシュを無効化して再取得
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.all() });
    },
  });
}

/**
 * 企業を更新するMutation Hook
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async ({
      companyId,
      request,
    }: {
      companyId: string;
      request: UpdateCompanyRequest;
    }) => {
      const authHeaders = getAuthHeader();
      return updateCompanyAuthenticated(companyId, request, authHeaders);
    },
    // 企業一覧と詳細のキャッシュを無効化して再取得
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.all() });
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(variables.companyId) });
    },
  });
}

/**
 * 企業を削除するMutation Hook
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (companyId: string) => {
      const authHeaders = getAuthHeader();
      return deleteCompanyAuthenticated(companyId, authHeaders);
    },
    // 企業一覧のキャッシュを無効化して再取得
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.all() });
    },
  });
}

/**
 * 企業を強制削除するMutation Hook（fkdev環境のみ）
 */
export function useForceDeleteCompany() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (companyId: string) => {
      const authHeaders = getAuthHeader();
      return forceDeleteCompanyAuthenticated(companyId, authHeaders);
    },
    // 削除された企業の関連クエリをキャッシュから削除し、一覧を再取得
    onSuccess: (_, companyId) => {
      // 削除された企業の詳細・関連データはキャッシュから削除（再フェッチしない）
      queryClient.removeQueries({ queryKey: companyKeys.detail(companyId) });
      queryClient.removeQueries({ queryKey: companyKeys.accounts(companyId) });
      queryClient.removeQueries({ queryKey: companyKeys.users(companyId) });
      queryClient.removeQueries({ queryKey: companyKeys.emailTemplates(companyId) });
      // 企業一覧は再取得
      queryClient.invalidateQueries({ queryKey: companyKeys.list() });
    },
  });
}

/**
 * 企業に紐づくアカウント一覧を取得するQuery Hook
 * @param companyId 企業ID
 */
export function useCompanyAccounts(companyId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: companyKeys.accounts(companyId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getCompanyAccountsAuthenticated(companyId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!companyId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 企業に紐づくユーザー一覧を取得するQuery Hook
 * @param companyId 企業ID
 */
export function useCompanyUsers(companyId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: companyKeys.users(companyId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getCompanyUsersAuthenticated(companyId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!companyId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 未紐づけアカウント一覧を取得するQuery Hook
 */
export function useUnlinkedAccounts() {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: ['mise', 'unlinked-accounts'],
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getUnlinkedAccountsAuthenticated(authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 企業のメールテンプレートを取得するQuery Hook
 * @param companyId 企業ID
 */
export function useCompanyEmailTemplates(companyId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: companyKeys.emailTemplates(companyId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getCompanyEmailTemplatesAuthenticated(companyId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!companyId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * 企業のメールテンプレートを更新するMutation Hook
 * @param companyId 企業ID
 */
export function useUpdateCompanyEmailTemplates(companyId: string) {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (request: UpdateEmailTemplatesRequest) => {
      const authHeaders = getAuthHeader();
      return updateCompanyEmailTemplatesAuthenticated(companyId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.emailTemplates(companyId) });
    },
  });
}

/**
 * バッチ実行SQSメッセージを送信するMutation Hook
 */
export function useExecuteBatch() {
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async ({
      accountId,
      forceAction,
      invokeBatchCache = true,
      invokeBatchLlm = true,
      forceDailySalesInitialize = false,
      invokeBatchStoreUsage = true,
    }: {
      accountId: string;
      forceAction: boolean;
      invokeBatchCache?: boolean;
      invokeBatchLlm?: boolean;
      forceDailySalesInitialize?: boolean;
      invokeBatchStoreUsage?: boolean;
    }) => {
      const authHeaders = getAuthHeader();
      return executeBatchAuthenticated(
        accountId,
        forceAction,
        invokeBatchCache,
        invokeBatchLlm,
        forceDailySalesInitialize,
        invokeBatchStoreUsage,
        authHeaders,
      );
    },
  });
}

/**
 * 企業にアカウントを紐づけるMutation Hook
 */
export function useLinkAccountToCompany() {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async ({ companyId, accountId }: { companyId: string; accountId: string }) => {
      const authHeaders = getAuthHeader();
      return linkAccountToCompanyAuthenticated(companyId, accountId, authHeaders);
    },
    // 企業詳細、アカウント一覧、未紐づけアカウント一覧のキャッシュを無効化
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: companyKeys.accounts(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: ['mise', 'unlinked-accounts'] });
    },
  });
}
